import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useAuthContext } from '../../../core/auth/hooks/useAuthContext'
import {
  acceptedAgreement,
  AgreementHistory,
  agreementUid,
  rememberAgreement,
} from '../../../helpers/driverAgreement'
import { driverAgreements } from '../../../services/api/requests/driverAgreements/driverAgreements'
import { AgreementRecord } from '../../../types/driverAgreements'
import { queriesKeys } from '../queriesKeys'

export const useDriverAgreements = (cityHint?: number | string) => {
  const { user } = useAuthContext()
  const uid = agreementUid(user)
  const client = useQueryClient()
  const activeUid = useRef(uid)
  activeUid.current = uid
  const [history, setHistory] = useState<AgreementHistory>({
    records: {},
    uid: '',
  })
  const rememberAccepted = useCallback(
    (record?: AgreementRecord) => {
      if (uid && record?.accepted_at)
        setHistory((previous) =>
          activeUid.current === uid
            ? rememberAgreement(previous, uid, record)
            : previous
        )
    },
    [uid]
  )
  const status = useQuery({
    enabled: Boolean(uid),
    gcTime: 0,
    queryFn: ({ signal }) => driverAgreements.status(uid, signal),
    queryKey: queriesKeys.driverAgreements.status(uid, cityHint),
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    retry: false,
    staleTime: 0,
  })
  useEffect(() => {
    setHistory((previous) =>
      rememberAgreement(
        previous,
        uid,
        status.data?.highest_accepted_record ?? undefined
      )
    )
  }, [uid, status.dataUpdatedAt, status.data?.highest_accepted_record])
  useEffect(
    () => () => {
      client.cancelQueries({
        queryKey: queriesKeys.driverAgreements.all(uid),
      })
      client.removeQueries({ queryKey: queriesKeys.driverAgreements.all(uid) })
    },
    [client, uid]
  )
  return {
    ...status,
    acceptedRecord: acceptedAgreement(history, uid, status.data),
    rememberAccepted,
    uid,
  }
}
